import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { Badge, Button, Card, Input, Label, Section, TH, TD, Empty } from "../components/ui";
import { toast } from "sonner";
import { ArrowRight, Play, FlaskConical, Save, KeyRound } from "lucide-react";

const POLICY_OPTIONS = [
  ["block_after_export", "Blocca dopo export (sicuro)"],
  ["update_if_draft", "Aggiorna se bozza"],
  ["create_rectification", "Crea rettificativa"],
  ["ignore", "Ignora modifiche"],
];

export default function CompanyDetail() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState("general"); // general | credentials | taxes | series | sync | actions
  const [creds, setCreds] = useState({});
  const [taxes, setTaxes] = useState([]);
  const [series, setSeries] = useState([]);
  const [settings, setSettings] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/companies/${id}`);
    setCompany(data);
    const [c, t, s, ss] = await Promise.all([
      api.get(`/companies/${id}/credentials`),
      api.get(`/companies/${id}/tax-mappings`),
      api.get(`/companies/${id}/series-mappings`),
      api.get(`/companies/${id}/sync-settings`),
    ]);
    setCreds(c.data); setTaxes(t.data); setSeries(s.data); setSettings(ss.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!company) return <div className="text-ink-muted">Caricamento…</div>;

  return (
    <div className="space-y-6" data-testid="company-detail-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/companies" className="text-sm text-ink-muted hover:underline">← Società</Link>
          <h1 className="font-display text-2xl font-semibold tracking-tight mt-1">{company.name}</h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-ink-muted">
            <Badge tone={company.is_active ? "success" : "default"}>{company.is_active ? "Attiva" : "Disattiva"}</Badge>
            <span className="font-mono text-xs">Zoho org: {company.zoho_organization_id || "—"}</span>
            <span>•</span>
            <span className="font-mono text-xs">FD company: {company.fd_company_id || "—"}</span>
          </div>
        </div>
      </div>

      <div className="border-b border-border-default flex gap-1">
        {[
          ["general", "Generale"],
          ["credentials", "Credenziali"],
          ["taxes", "Mappatura Imposte"],
          ["series", "Mappatura Serie"],
          ["sync", "Scheduling"],
          ["actions", "Avvio Sync"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 ${tab === k ? "border-ink text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "general" && <GeneralTab company={company} reload={load} />}
      {tab === "credentials" && <CredentialsTab companyId={id} creds={creds} reload={load} />}
      {tab === "taxes" && <TaxMappingTab companyId={id} taxes={taxes} reload={load} />}
      {tab === "series" && <SeriesTab companyId={id} series={series} reload={load} />}
      {tab === "sync" && <SyncSettingsTab companyId={id} settings={settings} reload={load} />}
      {tab === "actions" && <ActionsTab companyId={id} />}
    </div>
  );
}

function GeneralTab({ company, reload }) {
  const [f, setF] = useState(company);
  useEffect(() => setF(company), [company]);
  const save = async () => {
    try {
      await api.put(`/companies/${company.id}`, f);
      toast.success("Salvato");
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <Card className="p-6 grid grid-cols-2 gap-4">
      <div className="col-span-2"><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
      <div><Label>Zoho Organization ID</Label><Input value={f.zoho_organization_id || ""} onChange={(e) => setF({ ...f, zoho_organization_id: e.target.value })} /></div>
      <div><Label>Zoho Base URL</Label><Input value={f.zoho_base_url || ""} onChange={(e) => setF({ ...f, zoho_base_url: e.target.value })} /></div>
      <div><Label>Custom field CIF</Label><Input value={f.zoho_cif_custom_field || ""} onChange={(e) => setF({ ...f, zoho_cif_custom_field: e.target.value })} placeholder="es: cif" /></div>
      <div><Label>FD Company ID</Label><Input value={f.fd_company_id || ""} onChange={(e) => setF({ ...f, fd_company_id: e.target.value })} /></div>
      <div><Label>FD Base URL</Label><Input value={f.fd_base_url || ""} onChange={(e) => setF({ ...f, fd_base_url: e.target.value })} /></div>
      <div className="col-span-2"><Label>Serie VeriFactu (FD)</Label>
        <Input value={f.fd_verifactu_series || ""} onChange={(e) => setF({ ...f, fd_verifactu_series: e.target.value })}
               placeholder="es: VF — codice serie FD non di tipo 'externa'"
               data-testid="input-fd-verifactu-series" />
        <div className="text-xs text-ink-muted mt-1">Usata solo dal flusso Sync VeriFactu. La serie deve esistere in FacturaDirecta e NON essere di tipo &quot;externa&quot;.</div>
      </div>
      <div><Label>Policy post-export</Label>
        <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                value={f.post_export_policy} onChange={(e) => setF({ ...f, post_export_policy: e.target.value })} data-testid="select-policy">
          {POLICY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></div>
      <div><Label>Soglia diff totale (EUR)</Label><Input type="number" step="0.01" value={f.total_diff_threshold} onChange={(e) => setF({ ...f, total_diff_threshold: parseFloat(e.target.value) })} /></div>
      <div><Label>Tipo nota credito default</Label>
        <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                value={f.credit_note_default_type || "R1"} onChange={(e) => setF({ ...f, credit_note_default_type: e.target.value })}>
          {["R1", "R2", "R3", "R4", "R5"].map((x) => <option key={x}>{x}</option>)}
        </select></div>
      <div className="flex items-center gap-2 mt-6">
        <input type="checkbox" id="active" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />
        <label htmlFor="active" className="text-sm">Società attiva</label>
        <input type="checkbox" id="dryd" className="ml-6" checked={f.dry_run_default} onChange={(e) => setF({ ...f, dry_run_default: e.target.checked })} />
        <label htmlFor="dryd" className="text-sm">Default dry-run</label>
      </div>
      <div className="col-span-2 flex justify-end">
        <Button onClick={save} data-testid="btn-save-general"><Save size={14} className="mr-1.5" /> Salva</Button>
      </div>
    </Card>
  );
}

const CRED_KEYS = {
  zoho: ["access_token", "client_id", "client_secret", "refresh_token", "accounts_url"],
  facturadirecta: ["api_key"],
};

function CredentialsTab({ companyId, creds, reload }) {
  const [draft, setDraft] = useState({ provider: "zoho", key_name: "access_token", value: "" });
  const save = async () => {
    if (!draft.value) return toast.error("Valore richiesto");
    try {
      await api.put(`/companies/${companyId}/credentials`, draft);
      toast.success("Credenziale salvata");
      setDraft({ ...draft, value: "" });
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const test = async (provider) => {
    const url = provider === "zoho" ? `/companies/${companyId}/test-zoho` : `/companies/${companyId}/test-facturadirecta`;
    try {
      const { data } = await api.post(url);
      data.ok ? toast.success(data.message) : toast.error(data.message);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="font-display font-medium text-lg mb-3 flex items-center gap-2"><KeyRound size={16} /> Aggiungi/aggiorna credenziale</h3>
        <div className="grid grid-cols-4 gap-3 items-end">
          <div><Label>Provider</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={draft.provider} data-testid="select-provider"
                    onChange={(e) => setDraft({ ...draft, provider: e.target.value, key_name: CRED_KEYS[e.target.value][0] })}>
              <option value="zoho">Zoho</option>
              <option value="facturadirecta">FacturaDirecta</option>
            </select></div>
          <div><Label>Chiave</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={draft.key_name} data-testid="select-key"
                    onChange={(e) => setDraft({ ...draft, key_name: e.target.value })}>
              {CRED_KEYS[draft.provider].map((k) => <option key={k} value={k}>{k}</option>)}
            </select></div>
          <div className="col-span-2"><Label>Valore (sarà cifrato)</Label><Input type="password" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} data-testid="input-cred-value" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={() => test("zoho")} data-testid="btn-test-zoho"><FlaskConical size={14} className="mr-1.5" /> Test Zoho</Button>
          <Button variant="secondary" onClick={() => test("facturadirecta")} data-testid="btn-test-fd"><FlaskConical size={14} className="mr-1.5" /> Test FD</Button>
          <Button onClick={save} data-testid="btn-save-cred"><Save size={14} className="mr-1.5" /> Salva</Button>
        </div>
      </Card>

      {["zoho", "facturadirecta"].map((p) => (
        <Card key={p} className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border-default font-medium capitalize">{p}</div>
          <table className="w-full">
            <thead className="bg-surface-hover/50 border-b border-border-default">
              <tr><TH>Chiave</TH><TH>Valore (mascherato)</TH></tr>
            </thead>
            <tbody>
              {Object.entries(creds[p] || {}).map(([k, v]) => (
                <tr key={k} className="border-b border-border-divider">
                  <TD className="font-mono text-xs">{k}</TD>
                  <TD className="font-mono text-xs">{v}</TD>
                </tr>
              ))}
              {Object.keys(creds[p] || {}).length === 0 && <tr><td colSpan={2}><Empty>Nessuna credenziale {p} configurata.</Empty></td></tr>}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}

function TaxMappingTab({ companyId, taxes, reload }) {
  const EMPTY = { zoho_tax_id: "", zoho_tax_name: "", zoho_tax_percentage: "", fd_tax_id: "", fd_tax_name: "", verifactu_operacion_exenta: "" };
  const [d, setD] = useState(EMPTY);
  const save = async () => {
    try {
      await api.post(`/companies/${companyId}/tax-mappings`, {
        ...d,
        zoho_tax_percentage: d.zoho_tax_percentage ? parseFloat(d.zoho_tax_percentage) : null,
        verifactu_operacion_exenta: d.verifactu_operacion_exenta || null,
      });
      toast.success("Mapping salvato");
      setD(EMPTY);
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const remove = async (mid) => {
    if (!window.confirm("Eliminare il mapping?")) return;
    await api.delete(`/companies/${companyId}/tax-mappings/${mid}`);
    toast.success("Eliminato"); reload();
  };
  const patchOperacionExenta = async (mid, value) => {
    try {
      await api.patch(`/companies/${companyId}/tax-mappings/${mid}`,
                      { verifactu_operacion_exenta: value || null });
      toast.success("Aggiornato");
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="font-display font-medium text-lg mb-3">Nuovo mapping imposta</h3>
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-2"><Label>Zoho Tax ID *</Label><Input value={d.zoho_tax_id} onChange={(e) => setD({ ...d, zoho_tax_id: e.target.value })} data-testid="input-zoho-tax-id" /></div>
          <div className="col-span-2"><Label>Nome (Zoho)</Label><Input value={d.zoho_tax_name} onChange={(e) => setD({ ...d, zoho_tax_name: e.target.value })} /></div>
          <div className="col-span-1"><Label>%</Label><Input type="number" step="0.01" value={d.zoho_tax_percentage} onChange={(e) => setD({ ...d, zoho_tax_percentage: e.target.value })} /></div>
          <div className="col-span-1 flex justify-center pb-2"><ArrowRight size={20} className="text-ink-muted" /></div>
          <div className="col-span-2"><Label>FD Tax ID *</Label><Input value={d.fd_tax_id} onChange={(e) => setD({ ...d, fd_tax_id: e.target.value })} data-testid="input-fd-tax-id" /></div>
          <div className="col-span-2">
            <Label title="Solo per tasse esenti VeriFactu. Lascia vuoto per IVA standard.">VeriFactu Exenta</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.verifactu_operacion_exenta || ""}
                    onChange={(e) => setD({ ...d, verifactu_operacion_exenta: e.target.value })}
                    data-testid="select-tax-operacion-exenta">
              <option value="">—</option>
              <option value="E1">E1 — Art. 20 LIVA</option>
              <option value="E2">E2 — Art. 21 (Export)</option>
              <option value="E3">E3 — Art. 22</option>
              <option value="E4">E4 — Art. 24</option>
              <option value="E5">E5 — Art. 25 (Intra-UE)</option>
              <option value="E6">E6 — Otras</option>
            </select>
          </div>
          <div className="col-span-2"><Button onClick={save} className="w-full" data-testid="btn-save-tax-mapping">Salva</Button></div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-hover/50 border-b border-border-default">
            <tr><TH>Zoho ID</TH><TH>Nome</TH><TH>%</TH><TH>→</TH><TH>FD ID</TH><TH>Nome FD</TH><TH>VF Exenta</TH><TH></TH></tr>
          </thead>
          <tbody>
            {taxes.map((t) => (
              <tr key={t.id} className="border-b border-border-divider">
                <TD className="font-mono text-xs">{t.zoho_tax_id}</TD>
                <TD>{t.zoho_tax_name || "—"}</TD>
                <TD className="tabular-nums">{t.zoho_tax_percentage ?? "—"}</TD>
                <TD><ArrowRight size={14} className="text-ink-muted" /></TD>
                <TD className="font-mono text-xs">{t.fd_tax_id}</TD>
                <TD>{t.fd_tax_name || "—"}</TD>
                <TD>
                  <select className="h-7 px-2 rounded border border-border-default text-xs bg-surface font-mono"
                          value={t.verifactu_operacion_exenta || ""}
                          onChange={(e) => patchOperacionExenta(t.id, e.target.value)}
                          data-testid={`select-vfexenta-${t.id}`}>
                    <option value="">—</option>
                    <option value="E1">E1 — Art. 20</option>
                    <option value="E2">E2 — Art. 21</option>
                    <option value="E3">E3 — Art. 22</option>
                    <option value="E4">E4 — Art. 24</option>
                    <option value="E5">E5 — Art. 25</option>
                    <option value="E6">E6 — Otras</option>
                  </select>
                </TD>
                <TD><button className="text-xs text-error hover:underline" onClick={() => remove(t.id)}>Elimina</button></TD>
              </tr>
            ))}
            {taxes.length === 0 && <tr><td colSpan={8}><Empty>Nessun mapping imposta.</Empty></td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SeriesTab({ companyId, series, reload }) {
  const [d, setD] = useState({ mode: "preserve_regex", zoho_regex: "^(?P<series>[A-Z]+)-(?P<number>\\d+)$", fd_series_default: "", priority: 0 });
  const save = async () => {
    try {
      await api.post(`/companies/${companyId}/series-mappings`, d);
      toast.success("Regola salvata"); reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const remove = async (mid) => { await api.delete(`/companies/${companyId}/series-mappings/${mid}`); reload(); };
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="font-display font-medium text-lg mb-3">Nuova regola serie/numerazione</h3>
        <div className="grid grid-cols-5 gap-3 items-end">
          <div><Label>Modalità</Label>
            <select className="w-full h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={d.mode} onChange={(e) => setD({ ...d, mode: e.target.value })} data-testid="select-series-mode">
              <option value="preserve_regex">Preserva con regex</option>
              <option value="fixed_series">Serie fissa FD</option>
              <option value="prefix_map">Mapping per prefisso</option>
            </select></div>
          <div className="col-span-2"><Label>Regex Zoho (opzionale)</Label>
            <Input value={d.zoho_regex || ""} onChange={(e) => setD({ ...d, zoho_regex: e.target.value })}
                   placeholder="^(?P<series>[A-Z]+)-(?P<number>\d+)$" data-testid="input-series-regex" /></div>
          <div><Label>Serie FD default</Label>
            <Input value={d.fd_series_default || ""} onChange={(e) => setD({ ...d, fd_series_default: e.target.value })} data-testid="input-fd-series-default" /></div>
          <div><Label>Priorità</Label>
            <Input type="number" value={d.priority} onChange={(e) => setD({ ...d, priority: parseInt(e.target.value) || 0 })} data-testid="input-series-priority" /></div>
          <div className="col-span-5 flex justify-end"><Button onClick={save} data-testid="btn-save-series">Salva regola</Button></div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-hover/50 border-b border-border-default">
            <tr><TH>Modalità</TH><TH>Regex</TH><TH>Serie default</TH><TH>Priorità</TH><TH></TH></tr>
          </thead>
          <tbody>
            {series.map((s) => (
              <tr key={s.id} className="border-b border-border-divider">
                <TD className="font-mono text-xs">{s.mode}</TD>
                <TD className="font-mono text-xs">{s.zoho_regex || "—"}</TD>
                <TD className="font-mono text-xs">{s.fd_series_default || "—"}</TD>
                <TD className="tabular-nums">{s.priority}</TD>
                <TD><button className="text-xs text-error hover:underline" onClick={() => remove(s.id)}>Elimina</button></TD>
              </tr>
            ))}
            {series.length === 0 && <tr><td colSpan={5}><Empty>Nessuna regola.</Empty></td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SyncSettingsTab({ companyId, settings, reload }) {
  const upsert = async (entity, patch) => {
    const cur = settings.find((s) => s.entity === entity) || { entity, enabled: false, frequency_hours: 24 };
    await api.put(`/companies/${companyId}/sync-settings`, { ...cur, ...patch });
    reload();
  };
  return (
    <Card className="overflow-hidden">
      <table className="w-full">
        <thead className="bg-surface-hover/50 border-b border-border-default">
          <tr><TH>Entità</TH><TH>Abilitato</TH><TH>Frequenza (ore)</TH><TH>Ultima esecuzione</TH><TH>Prossima esecuzione</TH></tr>
        </thead>
        <tbody>
          {["contacts", "invoices", "credit_notes"].map((ent) => {
            const s = settings.find((x) => x.entity === ent) || { entity: ent, enabled: false, frequency_hours: 24 };
            return (
              <tr key={ent} className="border-b border-border-divider">
                <TD className="font-medium">{ent}</TD>
                <TD>
                  <input type="checkbox" checked={!!s.enabled}
                         data-testid={`toggle-${ent}`}
                         onChange={(e) => upsert(ent, { enabled: e.target.checked })} />
                </TD>
                <TD>
                  <Input type="number" min={1} className="w-24"
                         value={s.frequency_hours}
                         data-testid={`freq-${ent}`}
                         onBlur={(e) => upsert(ent, { frequency_hours: parseInt(e.target.value) || 24 })}
                         onChange={(e) => { s.frequency_hours = e.target.value; }} />
                </TD>
                <TD className="font-mono text-xs">{s.last_run_at ? new Date(s.last_run_at).toLocaleString("it-IT") : "—"}</TD>
                <TD className="font-mono text-xs">{s.next_run_at ? new Date(s.next_run_at).toLocaleString("it-IT") : "—"}</TD>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function ActionsTab({ companyId }) {
  const [busy, setBusy] = useState(null);
  // Period filter (applies only to invoices / credit_notes / all)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 2015; y--) years.push(y);
  const [periodMode, setPeriodMode] = useState("none"); // none | year | quarter | month
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(1);
  const [month, setMonth] = useState(1);

  const computeRange = () => {
    if (periodMode === "none") return { date_from: null, date_to: null };
    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m=1..12
    if (periodMode === "year") {
      return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
    }
    if (periodMode === "quarter") {
      const startMonth = (quarter - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      return {
        date_from: `${year}-${pad(startMonth)}-01`,
        date_to: `${year}-${pad(endMonth)}-${pad(lastDay(year, endMonth))}`,
      };
    }
    // month
    return {
      date_from: `${year}-${pad(month)}-01`,
      date_to: `${year}-${pad(month)}-${pad(lastDay(year, month))}`,
    };
  };

  const start = async (entity, mode) => {
    const key = `${entity}-${mode}`;
    // Safety net: real sync of invoices/credit_notes/all without period filter is dangerous
    if (mode === "real" && entity !== "contacts" && periodMode === "none") {
      const ok = window.confirm(
        `⚠️ ATTENZIONE\n\nStai per lanciare un Sync REALE di "${entity}" senza filtro periodo.\nVerranno processate TUTTE le fatture Zoho (anche quelle vecchie 2015→oggi).\n\nSei sicuro di voler procedere?\n\n(Consigliato: scegli prima un anno o un trimestre)`
      );
      if (!ok) return;
    }
    setBusy(key);
    try {
      const body = { entity, mode };
      // Period filter applies only to invoices / credit_notes / all
      if (entity !== "contacts") {
        const range = computeRange();
        if (range.date_from) body.date_from = range.date_from;
        if (range.date_to) body.date_to = range.date_to;
      }
      const { data } = await api.post(`/sync/${companyId}/start`, body);
      toast.success(`Job avviato (id ${data.id.slice(0, 8)}…)`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  const range = computeRange();
  const rangeLabel = range.date_from
    ? `${range.date_from} → ${range.date_to}`
    : "tutte le date (no filtro)";

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="font-display text-lg font-medium">Filtro periodo (solo fatture / note credito)</div>
        <p className="text-sm text-ink-muted mt-1">
          Per evitare di importare tutto lo storico Zoho in un colpo solo. Il filtro è ignorato per i contatti.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <Label>Modalità</Label>
            <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                    value={periodMode} onChange={(e) => setPeriodMode(e.target.value)}
                    data-testid="select-period-mode">
              <option value="none">Nessun filtro (tutto)</option>
              <option value="year">Anno intero</option>
              <option value="quarter">Anno + Trimestre</option>
              <option value="month">Anno + Mese</option>
            </select>
          </div>
          {periodMode !== "none" && (
            <div>
              <Label>Anno</Label>
              <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                      value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                      data-testid="select-period-year">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          {periodMode === "quarter" && (
            <div>
              <Label>Trimestre</Label>
              <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                      value={quarter} onChange={(e) => setQuarter(parseInt(e.target.value))}
                      data-testid="select-period-quarter">
                <option value={1}>Q1 (Gen-Mar)</option>
                <option value={2}>Q2 (Apr-Giu)</option>
                <option value={3}>Q3 (Lug-Set)</option>
                <option value={4}>Q4 (Ott-Dic)</option>
              </select>
            </div>
          )}
          {periodMode === "month" && (
            <div>
              <Label>Mese</Label>
              <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                      value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                      data-testid="select-period-month">
                {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((m, i) =>
                  <option key={i+1} value={i+1}>{i+1} — {m}</option>)}
              </select>
            </div>
          )}
          <div className="text-xs text-ink-muted font-mono pb-1.5" data-testid="period-range-preview">
            {rangeLabel}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        {["contacts", "invoices", "credit_notes", "all"].map((entity) => (
          <Card key={entity} className="p-5">
            <div className="font-display text-lg font-medium capitalize">{entity}</div>
            <p className="text-sm text-ink-muted mt-1">
              Sincronizza {entity === "all" ? "tutte le entità in ordine" : entity} per questa società.
              {entity !== "contacts" && periodMode !== "none" && (
                <span className="block text-ink mt-1">📅 Periodo: <span className="font-mono">{rangeLabel}</span></span>
              )}
            </p>
            <div className="mt-4 flex gap-2">
              <Button variant="secondary" disabled={busy === `${entity}-dry_run`} onClick={() => start(entity, "dry_run")} data-testid={`btn-dry-${entity}`}>
                <FlaskConical size={14} className="mr-1.5" /> Dry-Run
              </Button>
              <Button disabled={busy === `${entity}-real`} onClick={() => start(entity, "real")} data-testid={`btn-real-${entity}`}>
                <Play size={14} className="mr-1.5" /> Esegui sync reale
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
